import React, { useState, useRef, useEffect, useContext } from 'react';
import { Container, Row, Col, Card, Form, Button, Image, Badge } from 'react-bootstrap';
import { 
  FaUser,
  FaEdit,
  FaSave,
  FaTimes,
  FaCamera,
  FaEnvelope,
  FaPhone,
  FaIdCard,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaLock,
  FaKey,
  FaBell
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import AppContext from '../../../../context/AppContext';
import '../../../../assets/css/customer/dashboard/CustomerProfile.css';
import CustomerLayout from '../../../../layouts/customer/CustomerLayout';

const CP_BASE_URL = 'http://localhost:8000/api/customer';

const CustomerProfile = () => {
  const { regexChecker } = useContext(AppContext);

  // Logged in customer ki information
  const loggedInCustomer = JSON.parse(localStorage.getItem("loggedInCustomer"));
  const CP_customerId = loggedInCustomer?.id;

  // Profile State
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    cnic: '',
    address: '',
    memberSince: '',
    profilePicture: null,
    profilePicturePreview: 'https://ui-avatars.com/api/?name=Customer&size=200&background=7fd3c1&color=fff&bold=true'
  });

  // Profile Validation Errors
  const [profileErrors, setProfileErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    cnic: ''
  });

  // Security State
  const [securityData, setSecurityData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Security Validation Errors
  const [securityErrors, setSecurityErrors] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  // Notification Settings State
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    inAppNotifications: true
  });

  const [CP_savingNotifications, setCP_savingNotifications] = useState(false);

  // Edit Mode States
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [tempProfileData, setTempProfileData] = useState({ ...profileData });

  // Loading States
  const [loading, setLoading] = useState({
    profile: false,
    security: false
  });

  const fileInputRef = useRef(null);

  // ===================== VIEW PROFILE (READ) =====================
  const CP_fetchProfile = () => {
    const formData = new FormData();
    formData.append('id', CP_customerId);

    fetch(`${CP_BASE_URL}/auth/view-profile`, {
      method: 'POST',
      body: formData
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.success) {
          const c = response.customer;
          const updated = {
            firstName: c.firstName || '',
            lastName: c.lastName || '',
            email: c.email || '',
            phone: c.phone || '',
            cnic: c.cnic || '',
            address: c.address || '',
            memberSince: c.created_at ? new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '',
            profilePicture: null,
            profilePicturePreview: c.profile_picture
              ? `http://localhost:8000/storage/${c.profile_picture}`
              : `https://ui-avatars.com/api/?name=${encodeURIComponent((c.firstName || 'Customer') + ' ' + (c.lastName || ''))}&size=200&background=7fd3c1&color=fff&bold=true`
          };
          setProfileData(updated);
          setTempProfileData(updated);
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: response.message || 'Unable to load profile',
            confirmButtonColor: '#7fd3c1'
          });
        }
      })
      .catch((error) => {
        console.error(error);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Something went wrong while loading profile',
          confirmButtonColor: '#7fd3c1'
        });
      });
  };

  // ===================== VIEW NOTIFICATION SETTINGS (READ) =====================
  const CP_fetchNotificationSettings = () => {
    const formData = new FormData();
    formData.append('id', CP_customerId);

    fetch(`${CP_BASE_URL}/auth/view-notification-settings`, {
      method: 'POST',
      body: formData
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.success) {
          const settings = response.settings;
          setNotificationSettings({
            emailNotifications: !!settings.emailNotifications,
            inAppNotifications: !!settings.inAppNotifications
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: response.message || 'Unable to load notification settings',
            confirmButtonColor: '#7fd3c1'
          });
        }
      })
      .catch((error) => {
        console.error(error);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Something went wrong while loading notification settings',
          confirmButtonColor: '#7fd3c1'
        });
      });
  };

  useEffect(() => {

    if (!CP_customerId) {
      Swal.fire({
        icon: "error",
        title: "Session Expired",
        text: "Please login again."
      });
      return;
    }

    CP_fetchProfile();
    CP_fetchNotificationSettings();

  }, [CP_customerId]);

  // ===================== UPDATE NOTIFICATION SETTINGS =====================
  const handleNotificationToggle = (field) => {
    const updatedSettings = {
      ...notificationSettings,
      [field]: !notificationSettings[field]
    };

    setNotificationSettings(updatedSettings);
    setCP_savingNotifications(true);

    const formData = new FormData();
    formData.append('id', CP_customerId);
    formData.append('emailNotifications', updatedSettings.emailNotifications ? 1 : 0);
    formData.append('inAppNotifications', updatedSettings.inAppNotifications ? 1 : 0);

    fetch(`${CP_BASE_URL}/auth/update-notification-settings`, {
      method: 'POST',
      body: formData
    })
      .then((res) => res.json())
      .then((response) => {
        if (!response.success) {
          Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: response.message || 'Unable to update notification settings',
            confirmButtonColor: '#7fd3c1'
          });
          // Revert toggle agar backend fail ho jaye
          CP_fetchNotificationSettings();
        }
      })
      .catch((error) => {
        console.error(error);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Something went wrong while updating notification settings',
          confirmButtonColor: '#7fd3c1'
        });
        CP_fetchNotificationSettings();
      })
      .finally(() => {
        setCP_savingNotifications(false);
      });
  };

  // Handle Profile Picture Upload
  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5000000) {
        Swal.fire({
          icon: 'error',
          title: 'File Too Large',
          text: 'Please select an image under 5MB',
          confirmButtonColor: '#7fd3c1'
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setTempProfileData({
          ...tempProfileData,
          profilePicture: file,
          profilePicturePreview: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle Edit Profile
  const handleEditProfile = () => {
    setIsEditingProfile(true);
    setTempProfileData({ ...profileData });
  };

  // Handle Cancel Edit
  const handleCancelEdit = () => {
    setIsEditingProfile(false);
    setTempProfileData({ ...profileData });
    setProfileErrors({ firstName: '', lastName: '', email: '', phone: '', cnic: '' });
  };

  // ===================== SAVE PROFILE (UPDATE) =====================
  const handleSaveProfile = () => {

    // Validation check
    const errors = {
      firstName: regexChecker('username', tempProfileData.firstName),
      lastName: regexChecker('username', tempProfileData.lastName),
      email: regexChecker('email', tempProfileData.email),
      phone: regexChecker('phoneNumber', tempProfileData.phone),
      cnic: regexChecker('CNIC', tempProfileData.cnic)
    };

    setProfileErrors(errors);

    if (errors.firstName || errors.lastName || errors.email || errors.phone || errors.cnic) {
      return;
    }

    setLoading({ ...loading, profile: true });

    const formData = new FormData();
    formData.append('id', CP_customerId);
    formData.append('firstName', tempProfileData.firstName);
    formData.append('lastName', tempProfileData.lastName);
    formData.append('email', tempProfileData.email);
    formData.append('phone', tempProfileData.phone);
    formData.append('cnic', tempProfileData.cnic);
    formData.append('address', tempProfileData.address);

    if (tempProfileData.profilePicture) {
      formData.append('profilePicture', tempProfileData.profilePicture);
    }

    fetch(`${CP_BASE_URL}/auth/update-profile`, {
      method: 'POST',
      body: formData
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.success) {
          Swal.fire({
            icon: 'success',
            title: 'Profile Updated!',
            text: 'Your profile has been updated successfully',
            confirmButtonColor: '#7fd3c1',
            timer: 2000,
            timerProgressBar: true
          });
          setIsEditingProfile(false);
          CP_fetchProfile();
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: response.message || 'Unable to update profile',
            confirmButtonColor: '#7fd3c1'
          });
        }
      })
      .catch((error) => {
        console.error(error);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Something went wrong while updating profile',
          confirmButtonColor: '#7fd3c1'
        });
      })
      .finally(() => {
        setLoading({ ...loading, profile: false });
      });
  };

  // ===================== CHANGE PASSWORD (PLAINTEXT) =====================
  const handleChangePassword = () => {

    if (securityData.newPassword !== securityData.confirmPassword) {
      Swal.fire({
        icon: 'error',
        title: 'Mismatch!',
        text: 'New password and confirm password must match',
        confirmButtonColor: '#7fd3c1'
      });
      return;
    }

    const errors = {
      newPassword: regexChecker('password', securityData.newPassword),
      confirmPassword: regexChecker('password', securityData.confirmPassword)
    };

    setSecurityErrors(errors);

    if (errors.newPassword || errors.confirmPassword) {
      return;
    }

    setLoading({ ...loading, security: true });

    const formData = new FormData();
    formData.append('id', CP_customerId);
    formData.append('newPassword', securityData.newPassword);

    fetch(`${CP_BASE_URL}/auth/change-password`, {
      method: 'POST',
      body: formData
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.success) {
          Swal.fire({
            icon: 'success',
            title: 'Password Changed!',
            text: 'Your password has been updated successfully',
            confirmButtonColor: '#7fd3c1',
            timer: 2000,
            timerProgressBar: true
          });
          setSecurityData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
          setSecurityErrors({ newPassword: '', confirmPassword: '' });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error!',
            text: response.message || 'Unable to change password',
            confirmButtonColor: '#7fd3c1'
          });
        }
      })
      .catch((error) => {
        console.error(error);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Something went wrong while changing password',
          confirmButtonColor: '#7fd3c1'
        });
      })
      .finally(() => {
        setLoading({ ...loading, security: false });
      });
  };

  return (
    <CustomerLayout>
    <div className="cp-profile-page">
      <Container fluid>
        {/* Page Header */}
        <div className="cp-page-header">
          <h1 className="cp-page-title">Customer Profile</h1>
          <p className="cp-page-subtitle">Manage your personal information & settings</p>
        </div>

        {/* Profile Header Card */}
        <Card className="cp-profile-header-card">
          <Card.Body className="cp-profile-header-body">
            <Row className="align-items-center">
              <Col md={3} className="text-center">
                <div className="cp-profile-picture-wrapper">
                  <Image 
                    src={isEditingProfile ? tempProfileData.profilePicturePreview : profileData.profilePicturePreview} 
                    roundedCircle 
                    className="cp-profile-picture"
                  />
                  {isEditingProfile && (
                    <button 
                      type="button"
                      className="cp-upload-btn"
                      onClick={() => fileInputRef.current.click()}
                    >
                      <FaCamera />
                    </button>
                  )}
                </div>
                <input 
                  type="file" 
                  ref={fileInputRef}
                  onChange={handleProfilePictureChange}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
              </Col>
              <Col md={6}>
                <h2 className="cp-profile-name">{profileData.firstName} {profileData.lastName}</h2>
                <p className="cp-profile-email">
                  <FaEnvelope className="cp-info-icon" />
                  {profileData.email}
                </p>
                <p className="cp-member-since">
                  <FaCalendarAlt className="cp-info-icon" />
                  Member since: <Badge bg="info">{profileData.memberSince}</Badge>
                </p>
              </Col>
              <Col md={3} className="text-end">
                {!isEditingProfile ? (
                  <Button className="cp-btn-edit" onClick={handleEditProfile}>
                    <FaEdit className="me-2" />
                    Edit Profile
                  </Button>
                ) : (
                  <div className="cp-edit-buttons">
                    <Button className="cp-btn-save" onClick={handleSaveProfile} disabled={loading.profile}>
                      <FaSave className="me-2" />
                      {loading.profile ? 'Saving...' : 'Save'}
                    </Button>
                    <Button className="cp-btn-cancel" onClick={handleCancelEdit}>
                      <FaTimes className="me-2" />
                      Cancel
                    </Button>
                  </div>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* MAIN ROW - Two Columns Side by Side */}
        <Row>
          {/* LEFT COLUMN - Personal Information */}
          <Col lg={6}>
            <Card className="cp-info-card">
              <Card.Header className="cp-card-header">
                <div className="cp-card-header-with-button">
                  <h3 className="cp-card-title">
                    <FaUser className="cp-header-icon" />
                    Personal Information
                  </h3>
                  {!isEditingProfile ? (
                    <Button className="cp-btn-edit-header" onClick={handleEditProfile}>
                      <FaEdit className="me-2" />
                      Edit
                    </Button>
                  ) : (
                    <div className="cp-header-buttons">
                      <Button 
                        className="cp-btn-save-header" 
                        onClick={handleSaveProfile} 
                        disabled={loading.profile}
                      >
                        <FaSave className="me-2" />
                        {loading.profile ? 'Saving...' : 'Save'}
                      </Button>
                      <Button className="cp-btn-cancel-header" onClick={handleCancelEdit}>
                        <FaTimes className="me-2" />
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              </Card.Header>
              <Card.Body className="cp-card-body"> 
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label className="cp-form-label">
                      <FaUser className="cp-label-icon" />
                      First Name *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={isEditingProfile ? tempProfileData.firstName : profileData.firstName}
                      onChange={(e) => {
                        setTempProfileData({ ...tempProfileData, firstName: e.target.value });
                        setProfileErrors({ ...profileErrors, firstName: regexChecker('username', e.target.value) });
                      }}
                      placeholder="Enter your first name"
                      className={`cp-form-input ${profileErrors.firstName ? 'input-error' : ''}`}
                      readOnly={!isEditingProfile}
                    />
                    {isEditingProfile && profileErrors.firstName && (
                      <p style={{ color: 'red', fontSize: '13px' }}>{profileErrors.firstName}</p>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="cp-form-label">
                      <FaUser className="cp-label-icon" />
                      Last Name *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={isEditingProfile ? tempProfileData.lastName : profileData.lastName}
                      onChange={(e) => {
                        setTempProfileData({ ...tempProfileData, lastName: e.target.value });
                        setProfileErrors({ ...profileErrors, lastName: regexChecker('username', e.target.value) });
                      }}
                      placeholder="Enter your last name"
                      className={`cp-form-input ${profileErrors.lastName ? 'input-error' : ''}`}
                      readOnly={!isEditingProfile}
                    />
                    {isEditingProfile && profileErrors.lastName && (
                      <p style={{ color: 'red', fontSize: '13px' }}>{profileErrors.lastName}</p>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="cp-form-label">
                      <FaEnvelope className="cp-label-icon" />
                      Email Address *
                    </Form.Label>
                    <Form.Control
                      type="email"
                      value={isEditingProfile ? tempProfileData.email : profileData.email}
                      onChange={(e) => {
                        setTempProfileData({ ...tempProfileData, email: e.target.value });
                        setProfileErrors({ ...profileErrors, email: regexChecker('email', e.target.value) });
                      }}
                      placeholder="Enter your email"
                      className={`cp-form-input ${profileErrors.email ? 'input-error' : ''}`}
                      readOnly={!isEditingProfile}
                    />
                    {isEditingProfile && profileErrors.email && (
                      <p style={{ color: 'red', fontSize: '13px' }}>{profileErrors.email}</p>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="cp-form-label">
                      <FaPhone className="cp-label-icon" />
                      Phone Number *
                    </Form.Label>
                    <Form.Control
                      type="tel"
                      value={isEditingProfile ? tempProfileData.phone : profileData.phone}
                      onChange={(e) => {
                        setTempProfileData({ ...tempProfileData, phone: e.target.value });
                        setProfileErrors({ ...profileErrors, phone: regexChecker('phoneNumber', e.target.value) });
                      }}
                      placeholder="0300-XXXXXXX"
                      className={`cp-form-input ${profileErrors.phone ? 'input-error' : ''}`}
                      readOnly={!isEditingProfile}
                    />
                    {isEditingProfile && profileErrors.phone && (
                      <p style={{ color: 'red', fontSize: '13px' }}>{profileErrors.phone}</p>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="cp-form-label">
                      <FaIdCard className="cp-label-icon" />
                      CNIC *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={isEditingProfile ? tempProfileData.cnic : profileData.cnic}
                      onChange={(e) => {
                        setTempProfileData({ ...tempProfileData, cnic: e.target.value });
                        setProfileErrors({ ...profileErrors, cnic: regexChecker('CNIC', e.target.value) });
                      }}
                      placeholder="XXXXX-XXXXXXX-X"
                      className={`cp-form-input ${profileErrors.cnic ? 'input-error' : ''}`}
                      readOnly={!isEditingProfile}
                    />
                    {isEditingProfile && profileErrors.cnic && (
                      <p style={{ color: 'red', fontSize: '13px' }}>{profileErrors.cnic}</p>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="cp-form-label">
                      <FaMapMarkerAlt className="cp-label-icon" />
                      Address *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={isEditingProfile ? tempProfileData.address : profileData.address}
                      onChange={(e) => setTempProfileData({ ...tempProfileData, address: e.target.value })}
                      placeholder="Enter your address"
                      className="cp-form-input"
                      readOnly={!isEditingProfile}
                    />
                  </Form.Group>
                </Form>
              </Card.Body>
            </Card>

            {/* Notification Settings Card */}
            <Card className="cp-info-card">
              <Card.Header className="cp-card-header">
                <h3 className="cp-card-title">
                  <FaBell className="cp-header-icon" />
                  Notification Settings
                </h3>
              </Card.Header>
              <Card.Body className="cp-card-body">
                <div className="cp-notification-list">
                  <div className="cp-notification-item">
                    <div className="cp-notification-text">
                      <span className="cp-notification-title">Email Notifications</span>
                      <span className="cp-notification-desc">Receive updates via email</span>
                    </div>
                    <Form.Check
                      type="switch"
                      id="notif-email-notifications"
                      className="cp-notification-switch"
                      checked={notificationSettings.emailNotifications}
                      onChange={() => handleNotificationToggle('emailNotifications')}
                      disabled={CP_savingNotifications}
                    />
                  </div>

                  <div className="cp-notification-item">
                    <div className="cp-notification-text">
                      <span className="cp-notification-title">In-App Notifications</span>
                      <span className="cp-notification-desc">Show alerts inside the customer panel</span>
                    </div>
                    <Form.Check
                      type="switch"
                      id="notif-in-app-notifications"
                      className="cp-notification-switch"
                      checked={notificationSettings.inAppNotifications}
                      onChange={() => handleNotificationToggle('inAppNotifications')}
                      disabled={CP_savingNotifications}
                    />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* RIGHT COLUMN - Security Settings */}
          <Col lg={6}>
            <Card className="cp-info-card">
              <Card.Header className="cp-card-header">
                <h3 className="cp-card-title">
                  <FaLock className="cp-header-icon" />
                  Security Settings
                </h3>
              </Card.Header>
              <Card.Body className="cp-card-body">
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label className="cp-form-label">
                      <FaLock className="cp-label-icon" />
                      Current Password *
                    </Form.Label>
                    <Form.Control
                      type="password"
                      value={securityData.currentPassword}
                      onChange={(e) => setSecurityData({ ...securityData, currentPassword: e.target.value })}
                      placeholder="Enter current password"
                      className="cp-form-input"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="cp-form-label">
                      <FaKey className="cp-label-icon" />
                      New Password *
                    </Form.Label>
                    <Form.Control
                      type="password"
                      value={securityData.newPassword}
                      onChange={(e) => {
                        setSecurityData({ ...securityData, newPassword: e.target.value });
                        setSecurityErrors({ ...securityErrors, newPassword: regexChecker('password', e.target.value) });
                      }}
                      placeholder="Enter new password"
                      className={`cp-form-input ${securityErrors.newPassword ? 'input-error' : ''}`}
                    />
                    {securityErrors.newPassword && (
                      <p style={{ color: 'red', fontSize: '13px' }}>{securityErrors.newPassword}</p>
                    )}
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className="cp-form-label">
                      <FaKey className="cp-label-icon" />
                      Confirm Password *
                    </Form.Label>
                    <Form.Control
                      type="password"
                      value={securityData.confirmPassword}
                      onChange={(e) => {
                        setSecurityData({ ...securityData, confirmPassword: e.target.value });
                        setSecurityErrors({ ...securityErrors, confirmPassword: regexChecker('password', e.target.value) });
                      }}
                      placeholder="Re-enter new password"
                      className={`cp-form-input ${securityErrors.confirmPassword ? 'input-error' : ''}`}
                    />
                    {securityErrors.confirmPassword && (
                      <p style={{ color: 'red', fontSize: '13px' }}>{securityErrors.confirmPassword}</p>
                    )}
                  </Form.Group>

                  <Button 
                    className="cp-btn-save-full"
                    onClick={handleChangePassword}
                    disabled={loading.security}
                  >
                    <FaKey className="me-2" />
                    {loading.security ? 'Changing...' : 'Change Password'}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
    </CustomerLayout>
  );
};

export default CustomerProfile;